/**
 * Enhanced Cache Clearing Utility
 * Clears all types of browser storage and cache
 */

export interface CacheClearOptions {
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  clearCookies?: boolean;
  clearCacheStorage?: boolean;
  clearIndexedDB?: boolean;
  clearServiceWorker?: boolean;
  clearRedux?: boolean;
  clearRTKQuery?: boolean;
  reloadPage?: boolean;
}

export class EnhancedCacheClear {
  /**
   * Clear all browser storage and cache
   */
  static async clearAll(options: CacheClearOptions = {}): Promise<void> {
    const {
      clearLocalStorage = true,
      clearSessionStorage = true,
      clearCookies = true,
      clearCacheStorage = true,
      clearIndexedDB = true,
      clearServiceWorker = true,
      reloadPage = false
    } = options;

    // Clear localStorage
    if (clearLocalStorage) {
      this.clearLocalStorage();
    }

    // Clear sessionStorage
    if (clearSessionStorage) {
      this.clearSessionStorage();
    }

    // Clear cookies
    if (clearCookies) {
      this.clearCookies();
    }

    // Clear Cache Storage
    if (clearCacheStorage) {
      await this.clearCacheStorage();
    }

    // Clear IndexedDB
    if (clearIndexedDB) {
      await this.clearIndexedDB();
    }

    // Clear Service Worker cache
    if (clearServiceWorker) {
      await this.clearServiceWorkerCache();
    }

    // Reload page if requested
    if (reloadPage) {
      window.location.reload();
    }
  }

  /**
   * Clear localStorage
   */
  static clearLocalStorage(): void {
    localStorage.clear();
  }

  /**
   * Clear sessionStorage
   */
  static clearSessionStorage(): void {
    sessionStorage.clear();
  }

  /**
   * Clear all cookies
   */
  static clearCookies(): void {
    try {
      const cookies = document.cookie.split(';');
      
      cookies.forEach((cookie) => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        
        // Delete cookie with different path and domain combinations
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
      });
    } catch (error) {
      // Silent fail for cookies
    }
  }

  /**
   * Clear Cache Storage (Service Worker cache)
   */
  static async clearCacheStorage(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
      }
    } catch (error) {
      // Silent fail for cache storage
    }
  }

  /**
   * Clear IndexedDB
   */
  static async clearIndexedDB(): Promise<void> {
    try {
      if ('indexedDB' in window) {
        const databases = await window.indexedDB.databases();
        await Promise.all(
          databases.map((db) => {
            return new Promise<void>((resolve, reject) => {
              const request = window.indexedDB.deleteDatabase(db.name || '');
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            });
          })
        );
      }
    } catch (error) {
      // Silent fail for IndexedDB
    }
  }

  /**
   * Clear Service Worker cache and unregister
   */
  static async clearServiceWorkerCache(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => registration.unregister())
        );
      }
    } catch (error) {
      // Silent fail for service worker
    }
  }

  /**
   * Clear specific cache by name
   */
  static async clearSpecificCache(cacheName: string): Promise<void> {
    try {
      if ('caches' in window) {
        await caches.delete(cacheName);
      }
    } catch (error) {
      // Silent fail for specific cache
    }
  }

  /**
   * Clear all cache except specific ones
   */
  static async clearAllExcept(excludeCacheNames: string[]): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        const cachesToDelete = cacheNames.filter(
          (name) => !excludeCacheNames.includes(name)
        );
        
        await Promise.all(
          cachesToDelete.map((cacheName) => caches.delete(cacheName))
        );
      }
    } catch (error) {
      // Silent fail for caches
    }
  }

  /**
   * Get cache information
   */
  static async getCacheInfo(): Promise<{
    localStorage: number;
    sessionStorage: number;
    cookies: number;
    cacheStorage: string[];
    serviceWorkers: number;
  }> {
    const info = {
      localStorage: 0,
      sessionStorage: 0,
      cookies: 0,
      cacheStorage: [] as string[],
      serviceWorkers: 0
    };

    try {
      // Count localStorage items
      info.localStorage = localStorage.length;

      // Count sessionStorage items
      info.sessionStorage = sessionStorage.length;

      // Count cookies
      info.cookies = document.cookie.split(';').filter((cookie) => cookie.trim()).length;

      // Get cache storage names
      if ('caches' in window) {
        info.cacheStorage = await caches.keys();
      }

      // Count service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        info.serviceWorkers = registrations.length;
      }

      return info;
    } catch (error) {
      console.error('❌ Error getting cache info:', error);
      return info;
    }
  }
}

// Export default instance for easy usage
export default EnhancedCacheClear;
